import { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../proxy";
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Skeleton,
} from "@chakra-ui/react";

const DailyInsight = () => {
  const [insight, setInsight] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${proxy}server_mts/GetDailyInsight.php`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (res.data?.insight_text) {
          setInsight(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <Box
        maxW="900px"
        mx="auto"
        p={{ base: 3, md: 4 }}
        bg="rgba(45, 55, 72, 0.5)"
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        <Skeleton height="14px" mb={2} startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
        <Skeleton height="14px" width="80%" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
      </Box>
    );
  }

  if (!insight) return null;

  const date = new Date(insight.created_at);
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const change = parseFloat(insight.daily_change);
  const isPositive = change >= 0;

  return (
    <Box
      maxW="900px"
      mx="auto"
      position="relative"
      overflow="hidden"
    >
      <Box
        p={{ base: 3, md: 4 }}
        bg="linear-gradient(135deg, rgba(45, 55, 72, 0.7), rgba(26, 32, 44, 0.7))"
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.150"
        backdropFilter="blur(10px)"
        transition="all 0.3s"
        _hover={{
          borderColor: "whiteAlpha.250",
          boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
        }}
      >
        {/* Subtle gradient top accent */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="2px"
          bgGradient="linear(to-r, purple.400, teal.400)"
          opacity={0.6}
        />

        <VStack spacing={{ base: 2, md: 2.5 }} align="stretch">
          {/* Header row */}
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <HStack spacing={2}>
              <Badge
                bg="linear-gradient(135deg, #9F7AEA, #6B46C1)"
                color="white"
                fontSize={{ base: "2xs", md: "xs" }}
                px={2}
                py={0.5}
                borderRadius="full"
                fontWeight="bold"
              >
                🤖 AI Insight
              </Badge>
              <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                {formattedDate} • {formattedTime}
              </Text>
            </HStack>

            {change !== 0 && (
              <Badge
                colorScheme={isPositive ? "green" : "red"}
                fontSize={{ base: "2xs", md: "xs" }}
                px={2}
                py={0.5}
                borderRadius="full"
                variant="subtle"
              >
                {isPositive ? "↑" : "↓"} ${Math.abs(change).toFixed(2)} today
              </Badge>
            )}
          </HStack>

          {/* Insight text */}
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color="gray.200"
            lineHeight="tall"
            fontWeight="medium"
          >
            {insight.insight_text}
          </Text>

          {/* Footer */}
          <HStack spacing={1} justify="flex-end">
            <Text fontSize="2xs" color="gray.600" fontStyle="italic">
              Powered by Gemini AI
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default DailyInsight;